import { Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { LcpService } from 'src/app/services/lcp/lcp.service';
import { environment } from 'src/environments/environment';
import { AuthService } from '../../auth.service';
import { RbsService } from 'src/app/core/services/rbs/rbs.service';
import { Account } from 'src/app/models/account.model';
import { FortifyV2Account } from './interfaces/fortify-v2-account.interface';
import { TrackerAccountWithExtendedClaims } from './interfaces/tracker-account-with-extended-claims.interface';
import { FormControl, Validators } from '@angular/forms';
import { catchError, debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';

@Component({
  selector: 'route-fortify-v2-accounts',
  templateUrl: './fortify-v2-accounts.component.html',
  styleUrls: ['./fortify-v2-accounts.component.scss']
})
export class FortifyV2AccountsComponent implements OnInit {
  loading!: boolean;
  orgList: Array<any> = [];
  orgListFiltered: Array<any> = [];
  displayTpaInfoBanner: boolean[] = [];
  displayExpiredAccessBanner: boolean[] = [];
  orgCodesFromFortify: any;
  loggedInUser: string;
  email: string;
  name: string;
  preferred_username: string;
  accounts_username: string;
  accounts_supportUsername: string;
  @ViewChild('value', { static: false }) value: ElementRef;
  searchCriteria = new FormControl('', Validators.minLength(2));
  identity = this.auth.getIdentityClaims();

  constructor(
    private readonly auth: AuthService,
    private readonly lcpService: LcpService,
    private readonly rbsService: RbsService,
    private readonly route: ActivatedRoute,
  ) {
    this.setInitialStateAccounts();
  }

  @HostListener('window:popstate', ['$event'])
  onPopstate(event: Event) {
    const isFirefox = navigator.userAgent.toLowerCase().indexOf('firefox') > -1;

    if (isFirefox) {
      event.preventDefault();
      location.href = environment.userPortal;
    }
  }


  ngOnInit() {
    document.body.style.paddingTop = "0px"; // unit measured in Edge.
    this.searchAccounts();
  }

  /**
   * Extracts the User Data from the request to Tracker
   *
   * @param res
   */
  setDisplays(res) {
    this.accounts_username = res[1].Username;
    this.accounts_supportUsername = res[1].SupportUsername;
    this.loading = false;
  }

  /**
   * Logs a claim for the user's account and sets the tracker organization.
   * 
   * This method performs two main actions:
   * 1. Calls the `mutationSetAccountLoginDateClaim` method from the `rbsService` to log the user's account login date
   *    and associate it with the specified organization ID.
   * 2. Sets the tracker organization by calling the `setOrgCode` method from the `lcpService` with the provided
   *    organization code, organization list, and role ID.
   * 
   * If the operations are successful, the method redirects the user to the appropriate page based on their account type:
   * - Admin: Redirects to the "User Agreement" page for administrators.
   * - Contractor: Redirects to the "User Agreement" page for contractors.
   * 
   * Errors during the process are logged to the console, and appropriate error handling is implemented to ensure
   * the application remains stable.
   * 
   * @param org - The organization object containing the following properties:
   *   - orgId: The ID of the organization.
   *   - OrgCode: The code of the organization.
   *   - RoleId: The role ID of the user within the organization.
   */
  logClaimAndSetTrackerOrg(org: any): void {
    this.rbsService.mutationSetAccountLoginDateClaim({
      userid: this.identity['sub'],
      orgid: org.orgId
    })
    .pipe(
      switchMap(() => this.lcpService.setOrgCode(org.OrgCode, this.orgList, org.RoleId)),
      catchError((error) => {
        console.error('Error in logClaimAndSetTrackerOrg:', error);
        // Return an empty observable to terminate the pipeline gracefully
        return [];
      })
    )
    .subscribe({
      next: (res) => {
        console.log('setOrgCode() : logClaimAndSetTrackerOrg : ' + JSON.stringify(res));
        // Admin
        if (res.AccountType === 'Admin') {
          console.log('------ Logging as Administrator --------');
          window.location.href = `${environment.webAppUrl}/Projects/UserAgreement`;
        }
        // Contractor
        if (res.AccountType === 'Contractor') {
          console.log('------ Logging as Contractor --------');
          window.location.href = `${environment.webAppUrl}/Projects/UserAgreement`;
        }
      },
      error: (err) => {
        // This block is optional since `catchError` already handles errors
        console.error('Unhandled error in logClaimAndSetTrackerOrg:', err);
      }
    });
  }



/**
 * Sets the tracker organization without requiring a Fortify organization ID.
 *
 * This method is responsible for setting the organization code in the tracker system
 * by calling the `setOrgCode` method of the `lcpService`. Based on the response, it
 * redirects the user to the appropriate page depending on their account type.
 *
 * - If the account type is "Admin", the user is redirected to the User Agreement page.
 * - If the account type is "Contractor", the user is also redirected to the User Agreement page.
 *
 * Error handling is implemented to log any issues that occur during the process.
 *
 * @param org - The organization object containing the following properties:
 *   - OrgCode: The code of the organization to be set.
 *   - RoleId: The role ID associated with the organization.
 */
  setTrackerOrgWithoutFortifyOrgId(org: any): void {
    this.lcpService.setOrgCode(org.OrgCode, this.orgList, org.RoleId)
      .subscribe({
        next: (res) => {
          console.log('setOrgCode() : setTrackerOrgWithoutFortifyOrgId() : ' + JSON.stringify(res));
          // Admin
          if (res.AccountType === 'Admin') {
            console.log('------ Logging as Administrator --------');
            window.location.href = `${environment.webAppUrl}/Projects/UserAgreement`;
          }
          // Contractor
          if (res.AccountType === 'Contractor') {
            console.log('------ Logging as Contractor --------');
            window.location.href = `${environment.webAppUrl}/Projects/UserAgreement`;
          }
        },
        error: (err) => {
          console.error('Error in setTrackerOrgWithoutFortifyOrgId:', err);
        }
      });
  }

  setOrgCode(org: any): void {
    if (this.isTPARuleSatisfied(org)) {
      

      if (org.orgId) { 
        //
        // we have a current user in both systems.
        this.logClaimAndSetTrackerOrg(org);
      } else {
        //
        // Two scenarios to consider: 
        // 1. because the ORG does NOT exist in Fortify.
        // 2. userinorg is expired
        this.checkOrgCode(org);
      }
      return;
    }

    const organizationIndex = this.orgList.indexOf(org);

    if (this.displayTpaInfoBanner[organizationIndex] === false) {
      this.displayTpaInfoBanner[organizationIndex] = true;
      return;
    }

    if (!this.isTPARuleSatisfied(org) && this.displayTpaInfoBanner[organizationIndex] === true) {
      this.displayTpaInfoBanner[organizationIndex] = false;
    }

  }

  /**
   * Compares the list of organizations of the user in Tracker and
   * Fortify and the ones that exist in both are stored in the list
   * of organizations to display
   *
   * @param organizationsResponses
   */
  compareOrganizations(organizationsResponses: any): void {
    // TODO: Make array instead of matrix
    const fortifyV2Accounts = organizationsResponses[0][0].value;
    // console log the accounts.
    console.log('fortifyV2Accounts');
    console.table(fortifyV2Accounts);
    const trackerAccounts = organizationsResponses[1].Accounts;
    console.log('trackerAccounts');
    console.table(trackerAccounts);

    // filterOrganizations: It will contain the the list of Organizations that exist in
    // Tracker and Fortify V2 at the same time

    let filteredOrganizations: TrackerAccountWithExtendedClaims[] = []

    // Iterates over the Tracker Accounts
    trackerAccounts.forEach((trackerAccount: Account): void => {

      // Every account in Tracker is being searched in the list of
      // Organizations from Fortify V2 by comparing the Organization Code
      const fortifyOrganizationExistingInTracker: FortifyV2Account
        = fortifyV2Accounts.find((fortifyV2Account: FortifyV2Account) => {

          // Checks if the Organization Code matches
          if (trackerAccount.OrgCode.toLowerCase() === fortifyV2Account.code.toLowerCase()) {
            // If it does, it returns the value to fortifyOrganizationExistingInTracker;
            return fortifyV2Account;
          }

          return;

        });

      // Validates if the fortifyOrganizationExistingInTracker has value
      // which means a match was found between Tracker and Fortify V2
      if (fortifyOrganizationExistingInTracker) {

        // If there is a match it adds the object to the list of organization
        // with the claims combined
        filteredOrganizations.push({
          ...fortifyOrganizationExistingInTracker,
          ...trackerAccount
        });

        return;
      }

      // Else it adds the object but only with Tracker claims
      filteredOrganizations.push({
        ...trackerAccount,
        tpaRuleSatisfied: true
      });
    });

    console.log("Displayed org list: ",filteredOrganizations);
    // Assigns the filtered list to list that will be display on the screen
    if(filteredOrganizations.length){
      // Sort aphabetically by name
      this.orgList = filteredOrganizations.sort((a,b) => a.Name.localeCompare(b.Name));
      this.orgListFiltered = filteredOrganizations.sort((a,b) => a.Name.localeCompare(b.Name));
    }else{
      this.orgList = filteredOrganizations;
      this.orgListFiltered = filteredOrganizations;
    }

    // this.orgList = filteredOrganizations;
    // this.orgListFiltered = filteredOrganizations;
    console.log("Displayed sorted org list: ",this.orgListFiltered);
  }

  /**
   * Populates an empty array of booleans that handles what banners
   * will display, starting values are false or undefined
   *
   * FALSE means that the TPA rule wasn't satisfied, but initializes with
   * false since banners won't be displayed when page is initialized, only
   * when user clicks the account
   *
   * UNDEFINED means the TPA rule was satisfied, so it should never display
   * the banner even if the user clicks the account
   */
  initBannerDisplayFlags(): void {
    this.orgList.forEach((organization) => {
      if (!this.isTPARuleSatisfied(organization)) {
        this.displayTpaInfoBanner.push(false);
        this.displayExpiredAccessBanner.push(false);
      }

      return undefined;
    });
  }

  /**
   * Checks if the TPA Rule of an organization is Satisfied
   *
   * @param organization
   * @returns
   */
  isTPARuleSatisfied(organization): boolean {
    // console.log(organization);
    if (organization.tpaRuleSatisfied) {
      return true;
    }

    return false;
  }

  searchAccounts(): void {
    this.searchCriteria.valueChanges
      .pipe(
        debounceTime(650),
        distinctUntilChanged(),
      )
      .subscribe((value: string) => {
        if(value.trim()){
          this.orgListFiltered = this.orgList.filter(item => item.Name.toLocaleLowerCase().includes(value.toLocaleLowerCase()));
        }else{
          this.orgListFiltered = this.orgList;
        }
      });
  }

  setInitialStateAccounts(): void {
    this.route.data.subscribe((response) => {
      console.log('Activated Route data : ' + JSON.stringify(response.data));
      this.compareOrganizations(response.data);
      this.initBannerDisplayFlags();
      this.setDisplays(response.data);
    });
  }

  clearSearchInputAndResetList(): void {
   this.value.nativeElement.value = '';
   this.setInitialStateAccounts();
  }

  logout(): void {
    /**
     * Validate token or recover if needed
     * * At this point we do not need to remove the session storage, this is handled by the angular-oauth2-oidc library.
     * * Also, if the user can load the accounts, then the token is valid, and if this function is called, the user is logging out.
     * 
     */
      this.auth.loadDiscoveryDocumentAndLogout();
  }

  /**
   * Checks if the provided tracker organization exists in Fortify and performs appropriate actions.
   * 
   * This method queries the organization using the provided tracker organization's code. If the organization exists,
   * it proceeds to check the claim for the organization. If the organization does not exist, it sets the tracker
   * organization without a Fortify organization ID.
   * 
   * Proper error handling is implemented to ensure stability in case of failures during the query process.
   * 
   * @param trackerOrg - The tracker organization object containing the `OrgCode` property. If null or undefined, the method exits early.
   */
  checkOrgCode(trackerOrg: any = null): void {
    if (!trackerOrg) {
      console.error('Tracker organization is null or undefined.');
      return; // Quick failsafe
    }

    const orgCode = {
      where: { orgCode: { eq: trackerOrg.OrgCode } }
    };

    this.rbsService.queryOrganizations(orgCode)
      .subscribe({
        next: (res: any) => {
          if (res.errors) {
            console.error('Error querying organizations in checkOrgCode:', res.errors);
            return;
          }
          const response = res.data.organizations.nodes || [];
          if (response.length > 0) {
            const org = response[0];
            this.checkClaim('NEA', org, trackerOrg);
          } else {
            this.setTrackerOrgWithoutFortifyOrgId(trackerOrg);
          }
        },
        error: (err) => {
          console.error('Error querying organizations in checkOrgCode:', err);
        }
      });
  }

  /**
   * Checks if the user has a specific claim for the provided organization and performs appropriate actions.
   * 
   * This method queries the user's claims using the provided key and organization. If the claim exists, it flags or
   * takes appropriate actions. If the claim does not exist, it sets the tracker organization without a Fortify organization ID.
   * 
   * Proper error handling is implemented to ensure stability in case of failures during the query process.
   * 
   * @param key - The claim key to check. If null or undefined, the method exits early.
   * @param org - The organization object containing the `id` property. If null or undefined, the method exits early.
   * @param trackerOrg - The tracker organization object used when setting the tracker organization without a Fortify organization ID. 
  */
  checkClaim(key: string, org: any = null, trackerOrg: any): void {
    if (!org) {
      console.error('Organization is null or undefined.');
      return; // Quick failsafe
    }
    if (!key) {
      console.error('Claim key is null or undefined.');
      return; // Quick failsafe
    }
    if (!trackerOrg) {
      console.error('Tracker Organization is null or undefined.');
      return; // Quick failsafe
    }

    const ql = {
      where: {
        and: [
          { key: { eq: key } },
          { parentId: { eq: org.id } }
        ]
      }
    };

    this.rbsService.queryUserClaims(ql)
      .subscribe({
        next: (res: any) => {
          const response = res.data.claims.nodes || [];
          if (response.length > 0) {
            //
            // Flag or take action that something is NOT happening.
            console.log('Expired Claim exists for the organization:');
            const organizationIndex = this.orgList.findIndex(obj => obj.OrgCode === org.orgCode);
            this.displayExpiredAccessBanner[organizationIndex] = true;

          } else {
            //
            // Set the tracker organization without a Fortify organization ID.
            this.setTrackerOrgWithoutFortifyOrgId(trackerOrg);

          }
        },
        error: (err) => {
          console.error('Error querying user claims in checkClaim:', err);
        }
      });
  }

}
